import { HeroMinimal, BookingCTA } from "../components/sections";
import { Divider } from "../components/sections";
import { ChevronDown, CheckCircle } from "lucide-react";

const faqs = [
  {
    category: "What We Do",
    questions: [
      {
        question: "What does UniqReach do?",
        answer:
          "UniqReach builds scalable outbound sales systems for B2B technology companies using data, automation, AI, and outbound infrastructure. We combine prospect discovery, data enrichment, contact verification, AI research, personalization, automated outreach, follow-up, response handling, CRM workflows, reporting, and continuous optimization into a single system that operates continuously.",
      },
      {
        question: "Do you just send cold emails?",
        answer:
          "No. Email is one component. The system can include prospect discovery, data enrichment, verification, AI research, classification, segmentation, personalization, multi-channel outreach (email, LinkedIn, phone), follow-up automation, response classification, CRM synchronization, infrastructure management, deliverability optimization, and continuous performance optimization.",
      },
      {
        question: "Do you use AI?",
        answer:
          "Yes. AI supports research, enrichment, classification, segmentation, personalization, message generation, intent analysis, lead qualification, workflow decisions, follow-up logic, data processing, and campaign optimization. AI handles repetitive intelligence-heavy work so the system can operate at scale. Humans remain responsible for strategy, positioning, messaging direction, quality control, sales conversations, relationship building, and closing.",
      },
      {
        question: "Do you provide the leads?",
        answer:
          "Prospect discovery, enrichment, verification, and segmentation can be part of the engagement depending on requirements. We can build the complete data layer (discovery → enrichment → verification → segmentation) or integrate with your existing data sources.",
      },
      {
        question: "Do you handle deliverability?",
        answer:
          "Outbound infrastructure and deliverability are core parts of the system. This includes domain configuration, inbox management, authentication (SPF/DKIM/DMARC/BIMI), reputation monitoring, sending pattern optimization, bounce management, and placement testing.",
      },
    ],
  },
  {
    category: "Targeting & Fit",
    questions: [
      {
        question: "Can you target specific industries?",
        answer:
          "Yes. The system begins with a defined ICP and can be structured around industries, markets, company characteristics, technographics, buyer profiles, and intent signals. We work with B2B SaaS, AI companies, software companies, IT services, technology consultancies, B2B platforms, and tech-enabled services.",
      },
      {
        question: "Is this suitable for startups?",
        answer:
          "Potentially, if the company has a viable B2B offering, identifiable buyers, and a sufficiently clear customer profile. Early-stage companies with undefined ICP or pre-product-market fit may not be ready for a systematic outbound engine.",
      },
      {
        question: "What if we don't have a defined ICP?",
        answer:
          "ICP definition is the first stage of our system (Stage 01: Define). We can help you codify your ICP through structured discovery, historical win/loss analysis, and market validation. However, you need some foundational clarity on who you serve and why they buy.",
      },
    ],
  },
  {
    category: "Guarantees & Expectations",
    questions: [
      {
        question: "Do you guarantee meetings?",
        answer:
          "We do not promise guaranteed meetings unless a specific commercial agreement explicitly provides one. We build systems designed to produce qualified conversations at scale. Outcomes depend on market, product, positioning, and sales execution, factors partially outside system control.",
      },
      {
        question: "How quickly can campaigns launch?",
        answer:
          "We do not make fixed launch promises unless the actual engagement establishes one. Infrastructure setup (domains, warm-up, authentication) typically takes 2-4 weeks before scaled sending. Pilot sequences can begin during warm-up. Full system deployment is typically 6-12 weeks depending on complexity.",
      },
      {
        question: "How much does it cost?",
        answer:
          "We do not publish fixed pricing. Every outbound system is built around the company's market, ICP, sales goals, target volume, infrastructure requirements, and required level of automation. Engagements are scoped after a strategy call and discovery process.",
      },
    ],
  },
  {
    category: "Process & Operations",
    questions: [
      {
        question: "How do we get started?",
        answer:
          "Book a strategy call and provide information about your product, target market, current sales process, and growth objectives. We'll review, assess fit, and if there's alignment, discuss the appropriate system architecture and engagement model.",
      },
      {
        question: "What CRM integrations do you support?",
        answer:
          "We explicitly communicate compatibility with HubSpot, Salesforce, Pipedrive, and other CRM systems where technically compatible. The system connects your outbound engine to the CRM your sales team already uses. We do not claim native integrations with every CRM.",
      },
      {
        question: "Who owns the data and infrastructure?",
        answer:
          "You own your data. Infrastructure (domains, inboxes) can be set up in your accounts or managed by us depending on the engagement model. We believe in transparency and portability, avoiding vendor lock-in on your prospect data or sending infrastructure.",
      },
      {
        question: "What's the minimum engagement?",
        answer:
          "We don't publish fixed minimums. System design requires sufficient volume for statistical validity in optimization. Very low-volume needs (<500 contacts/month) may be better served by manual approaches or tools.",
      },
      {
        question: "How do you measure success?",
        answer:
          "Primary metrics: qualified conversations booked, sales-accepted opportunities created, pipeline value generated, reply rate, meeting-to-opportunity conversion, cost per qualified conversation. We do not lead with activity metrics (emails sent, contacts scraped, campaigns launched).",
      },
    ],
  },
];

function FAQPage() {
  return (
    <>
      <HeroMinimal
        title="Frequently Asked Questions"
        subtitle="Direct answers about how UniqReach works, who we work with, and what to expect. No marketing fluff."
        ctaText="Book a Strategy Call"
        ctaHref="/book"
        secondaryCtaText="Contact Us"
        secondaryCtaHref="/contact"
      />

      <section
        className="section"
        aria-label="Frequently asked questions by topic"
      >
        <div className="container">
          <div className="max-w-4xl mx-auto">
            {faqs.map((category, catIndex) => (
              <div
                key={category.category}
                className="animate-slide-up"
                style={{ animationDelay: `${catIndex * 100}ms` }}
              >
                <h2 className="h2 text-text-primary mb-6 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center text-accent">
                    <CheckCircle size={18} aria-hidden="true" />
                  </span>
                  {category.category}
                </h2>
                <div className="space-y-3 mb-12">
                  {category.questions.map((faq) => (
                    <details key={faq.question} className="group card p-0">
                      <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                        <h3 className="h3 text-text-primary pr-4">
                          {faq.question}
                        </h3>
                        <ChevronDown
                          size={20}
                          className="text-text-muted group-open:rotate-180 transition-transform flex-shrink-0"
                          aria-hidden="true"
                        />
                      </summary>
                      <div className="px-5 pb-5 pt-0 border-t border-border animate-slide-down">
                        <p className="body text-text-secondary">{faq.answer}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Still have questions */}
          <Divider />
          <div className="text-center mt-12">
            <h2 className="h1 md:display-sm text-text-primary mb-4">
              Still Have Questions?
            </h2>
            <p className="body-lg text-text-secondary mb-8 max-w-xl mx-auto">
              Can't find the answer you're looking for? Book a strategy call or
              send us a message. We'll give you a direct answer.
            </p>
            <CTA
              primaryText="Book a Strategy Call"
              primaryHref="/book"
              secondaryText="Contact Us"
              secondaryHref="/contact"
              align="center"
            />
          </div>
        </div>
      </section>

      <BookingCTA />
    </>
  );
}

import { CTA } from "../components/sections/CommonSections";

export default FAQPage;
